import html2canvas from "html2canvas";
import * as pdfMake from "pdfmake/build/pdfmake.js";
import * as pdfFonts from "pdfmake/build/vfs_fonts.js";
pdfMake.vfs = pdfFonts.pdfMake.vfs;

export default class ConvertElement2Pdf {
  /**
   * HTMLからPDFを生成
   */
  public async createPdfFromHtml(
    element: any,
    title: string,
    Orientation: PsfMakeOrientation,
    pageSize: PdfMakePageSize | PdfMakePage,
    autoPrint: boolean,
    output: PdfMakeOutputType
  ): Promise<any> {
    const pdfProps = await this.createPdfProps(element, title, Orientation, pageSize);
    const pdf: any = this.createPdf(pdfProps);

    // 出力種別毎に応じた形式で返す
    let base64Pdf: string = "";
    let blobPdf: Blob | undefined = undefined;
    switch (output) {
      case PdfMakeOutputType.PDFMAKE:
        return pdf;

      case PdfMakeOutputType.BASE64:
        base64Pdf = await this.convertBase64(pdf, autoPrint);
        return base64Pdf;

      case PdfMakeOutputType.BLOB:
        base64Pdf = await this.convertBase64(pdf, autoPrint);
        blobPdf = this.convertBlob(base64Pdf);
        return blobPdf;

      default:
        return pdf;
    }
  }

  /**
   * PDF用プロパティを作成
   */
  private async createPdfProps(element: HTMLElement, title: string, Orientation: PsfMakeOrientation, pageSize: PdfMakePageSize | PdfMakePage): Promise<PdfProps> {
    // scaleは2にしないとぼやけるので指定する
    const canvas = await html2canvas(element, { scale: 2 });
    const dataUrl = canvas.toDataURL();

    const pdfProps: PdfProps = {
      dataUrl,
      pageSize: pageSize,
      pageOrientation: Orientation,
      contentSize: {
        width: element.offsetWidth,
        height: element.clientHeight
      },
      info: {
        title: title,
        author: "KONICAMINOLA"
      },
      pageMargins: [0, 0]
    };

    return pdfProps;
  }

  /**
   * エンコードされた画像URLを貼り付けたPDFを出力する
   */
  private createPdf(pdfProps: PdfProps): any {
    const pageMargins = pdfProps.pageMargins;
    const pageSize = pdfProps.pageSize;
    const pageOrientation = pdfProps.pageOrientation;
    const info = pdfProps.info;

    const documentDefinitions = {
      pageSize,
      pageOrientation,
      content: {
        image: pdfProps.dataUrl,
        ...pdfProps.contentSize
      },
      info: info,
      pageMargins
    };

    return pdfMake.createPdf(documentDefinitions);
  }

  /**
   * PDFをBase64文字列に変換する
   *
   * @param pdf PdfMakeオブジェクト
   */
  private async convertBase64(pdf: any, autoPrint: boolean): Promise<string> {
    const getBase64 = new Promise<any>((resolve) => {
      pdf.getBase64(
        (data: any) => {
          // Base64エンコードしても、コンテンツタイプが格納されていないので、コンテンツタイプを先頭につける
          resolve("data:application/pdf;base64," + data);
        },
        { autoPrint: autoPrint }
      );
    });

    return await getBase64;
  }

  /**
   * Base64文字列をBlobに変換する
   *
   * @param base64 Base64文字列
   */
  private convertBlob(base64: string): Blob {
    const byteString = atob(base64.split(",")[1]);
    const byteLength: number = byteString.length;
    const content: Uint8Array = new Uint8Array(byteLength);
    for (let i = 0; i < byteLength; i++) {
      content[i] = byteString.charCodeAt(i);
    }

    const blob = new Blob([content], { type: "application/pdf" });

    return blob;
  }
}

/**
 * PDF用プロパティ
 */
interface PdfProps {
  dataUrl: string;
  pageSize?: PdfMakePageSize | PdfMakePage;
  pageOrientation?: PsfMakeOrientation;
  contentSize?: {
    width: number;
    height: number;
  };
  info: any;
  pageMargins?: [number, number];
}

export enum PsfMakeOrientation {
  LANDSCAPE = "landscape",
  PORTRAIT = "portrait"
}

export interface PdfMakePageSize {
  width: number;
  height: number;
}

export enum PdfMakePage {
  "4A0" = "4A0",
  "2A0" = "2A0",
  "A0" = "A0",
  "A1" = "A1",
  "A2" = "A2",
  "A3" = "A3",
  "A4" = "A4",
  "A5" = "A5",
  "A6" = "A6",
  "A7" = "A7",
  "A8" = "A8",
  "A9" = "A9",
  "A10" = "A10",
  "B0" = "B0",
  "B1" = "B1",
  "B2" = "B2",
  "B3" = "B3",
  "B4" = "B4",
  "B5" = "B4",
  "B6" = "B6",
  "B7" = "B7",
  "B8" = "B8",
  "B9" = "B9",
  "B10" = "B10"
}

export enum PdfMakeOutputType {
  PDFMAKE = "pdfmake",
  BASE64 = "base64",
  BLOB = "blob"
}
